import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isPossiblePhoneNumber } from "libphonenumber-js";

import EmailInput from "../EmailInput";
import CountryCode from "../CountryCode";
import CustomButton from "../CustomButton";

import { isOnlyNumbers, isValidEmail } from "../../utils/helpers";
import { ReactComponent as DeleteIcon } from "../../static/images/delete_icon.svg";
import { ReactComponent as EmailIcon } from "../../static/images/email.svg";
import { ReactComponent as PhoneIcon } from "../../static/images/dial-icon.svg";

import "./style.scss";

const InviteCustomers = memo(
  ({
    inviteCutomer,
    deleteCustomer,
    invitedCustomers,
    maxNumber,
    setInviteInputValue,
    errorfromparent,
    onFocus,
    boldTitle = true,
  }) => {
    const [selected, setSelected] = useState("mobile");
    const [inputValue, setInputValue] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [phoneHasError, setPhoneHasError] = useState(false);
    const limitReached = Number(maxNumber) === invitedCustomers.length;
    const { t } = useTranslation();

    const resetError = () => {
      setErrorMsg("");
    };

    const handleToggle = (active) => {
      setSelected(active);
      resetError();
    };

    const handleEmailInputChange = (e) => {
      setInputValue(e.target.value);

      if (setInviteInputValue) setInviteInputValue(e.target.value);
    };

    const handlePhoneInputChange = (value) => {
      setInputValue(value);
      setPhoneHasError(false);

      if (setInviteInputValue) setInviteInputValue(value);
    };

    const handleFocus = (e) => {
      resetError();

      if (onFocus) onFocus();
    };

    const handleInvite = () => {
      if (selected === "email" && !isValidEmail(inputValue)) return;

      if (selected === "mobile" && !isPossiblePhoneNumber("+" + inputValue)) {
        setPhoneHasError(true);
        return;
      }

      const alreadyInvited = invitedCustomers.some(
        (obj) => obj.contact === inputValue,
      );

      if (alreadyInvited) {
        setErrorMsg(t("createSchedule.inviteCustomers.error_already_invited"));
        return;
      }

      inviteCutomer(inputValue);
      setInputValue("");
      if (setInviteInputValue) setInviteInputValue("");
    };

    useEffect(() => {
      if (errorfromparent) {
        setErrorMsg(
          t("createSchedule.inviteCustomers.error_confirm_invitation"),
        );
      } else {
        resetError();
      }
    }, [errorfromparent]);

    return (
      <>
        <h3 className={`invitation-title ${boldTitle ? "bold" : ""}`}>
          {t(
            Number(maxNumber) === 1
              ? "createSchedule.inviteCustomers.title_individual"
              : "createSchedule.inviteCustomers.title_group",
          )}
        </h3>

        {limitReached ? (
          <p className="limit-reached">
            {t("createSchedule.inviteCustomers.error_max_reached", {
              count: maxNumber,
            })}
          </p>
        ) : (
          <>
            <div className="toggle-container">
              <span
                className={`toggle-label ${selected === "mobile" ? "active" : ""}`}
                onClick={() => handleToggle("mobile")}
              >
                {t("createSchedule.inviteCustomers.toggle_mobile")}
              </span>

              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="toggle"
                  className="toggle-input"
                  checked={selected === "email"}
                  onChange={() =>
                    handleToggle(selected === "mobile" ? "email" : "mobile")
                  }
                />
                <label htmlFor="toggle" className="toggle-label-switch"></label>
              </div>

              <span
                className={`toggle-label ${selected === "email" ? "active" : ""}`}
                onClick={() => handleToggle("email")}
              >
                {t("createSchedule.inviteCustomers.toggle_email")}
              </span>
            </div>
            <div className="invite-inputs-wrapper">
              {selected === "mobile" ? (
                <CountryCode
                  phone={inputValue}
                  changePhone={handlePhoneInputChange}
                  disability={limitReached}
                  onFocus={handleFocus}
                  hasError={phoneHasError}
                />
              ) : (
                <EmailInput
                  email={inputValue}
                  onChange={handleEmailInputChange}
                  disabled={limitReached}
                  onFocus={handleFocus}
                />
              )}
            </div>

            {!!errorMsg && <p className="error-msg">{errorMsg}</p>}

            <div className="add-button-wrapper">
              <CustomButton onClick={handleInvite} variant="outlined">
                {t("createSchedule.inviteCustomers.add_customer")}
              </CustomButton>
            </div>
          </>
        )}

        <div className="customers-wrapper">
          {invitedCustomers.map((customer) => (
            <div key={customer.id} className="invited-customer">
              <p>
                {isOnlyNumbers(customer.contact) ? (
                  <PhoneIcon />
                ) : (
                  <EmailIcon />
                )}
                {customer.contact}
              </p>
              <button onClick={() => deleteCustomer(customer.id)} type="button">
                <DeleteIcon />
              </button>
            </div>
          ))}
        </div>
      </>
    );
  },
);

export default InviteCustomers;
